import {useState} from 'react';
import {Alert, Button, Dialog, Input} from '@vector';
import {requestPasswordReset} from '../homeService';
import {getRecaptchaToken} from '../../portal/recaptcha';

/**
 * Forgotten password.
 *
 * Posts to `/web/ui/home/public/resetPasswordEmail`, which wraps the same
 * `userService.sendResetPasswordEmail` the Struts page and the portal both use.
 *
 * **The interesting case is one address with several accounts.** The service cannot guess which one, so it
 * answers `usernameRequired` and the dialog asks for a username and tries again — rather than failing with
 * something unhelpful. The jQuery version did this by searching the returned HTML for
 * `[data-duplicate='true']` (app.js:130-165); here it is a field on the response.
 *
 * The success wording is deliberately non-committal about whether the address is known. Confirming it
 * would turn this into a way to find out which addresses have accounts, and the endpoint says the same
 * thing for "sent" and "no such account" for that reason.
 */
export default function ForgotPassword({open, onClose}) {
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [needsUsername, setNeedsUsername] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const close = () => {
        setResult(null);
        setNeedsUsername(false);
        setUsername('');
        onClose();
    };

    const submit = async (event) => {
        event.preventDefault();
        setBusy(true);
        setResult(null);

        try {
            const recaptchaToken = await getRecaptchaToken('resetPassword');
            const outcome = await requestPasswordReset({email, username, recaptchaToken});

            setResult(outcome);
            if (outcome.usernameRequired) {
                setNeedsUsername(true);
            }
        } catch (e) {
            setResult({sent: false, message: 'We could not send the reset email. Please try again.'});
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog title="Forgotten your password?" open={open} onCancel={close} footer={null} destroyOnHidden>
            <form onSubmit={submit}>
                {/* Announced, so the duplicate-account prompt is not a silent change for a screen reader. */}
                <div aria-live="polite">
                    {result && (
                        <Alert
                            className="ch-site-alert"
                            type={result.sent ? 'success' : (result.usernameRequired ? 'warning' : 'error')}
                            message={result.message}
                            showIcon
                        />
                    )}
                </div>

                {!result?.sent && (
                    <>
                        <p>Enter your email address and we will send you a link to reset your password.</p>

                        <label className="ch-site-field-label" htmlFor="ch-home-reset-email">
                            Email address
                        </label>
                        <Input
                            id="ch-home-reset-email"
                            type="email"
                            required
                            autoFocus
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />

                        {needsUsername && (
                            <>
                                <label className="ch-site-field-label" htmlFor="ch-home-reset-username">
                                    Username
                                </label>
                                <Input
                                    id="ch-home-reset-username"
                                    autoComplete="username"
                                    required
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                />
                            </>
                        )}

                        <div className="ch-site-actions">
                            <Button type="primary" htmlType="submit" loading={busy} block>
                                Send reset link
                            </Button>
                        </div>
                    </>
                )}

                {result?.sent && (
                    <div className="ch-site-actions">
                        <Button onClick={close} block>Close</Button>
                    </div>
                )}
            </form>
        </Dialog>
    );
}
